/* eslint-disable no-unused-vars */
import { useState, useRef, useCallback, Dispatch, SetStateAction, MutableRefObject } from 'react';
import { ERROR_CODES } from '~/constants/errorCodes.ts';
import { errorMessages } from '~/constants/errorMessages.ts';

type VoidFunction = () => void;

interface UseRetryAlertReturn {
  isOpen: boolean;
  text: string;
  retryAction: VoidFunction | null;
  retryHandlerRef: MutableRefObject<VoidFunction | null>;
  closeAlert: () => void;
  showError: (message: string) => void;
  handleRetryableError: (
    errorMessage: string,
    errorCode: number | string | undefined,
    onPreRetry?: VoidFunction,
    onMaxRetries?: VoidFunction
  ) => void;
  handleTimeout: (onPreRetry?: VoidFunction, onMaxRetries?: VoidFunction) => void;
  setText: Dispatch<SetStateAction<string>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  resetFailureCount: () => void;
}

export const useRetryAlert = (): UseRetryAlertReturn => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>('');
  const [retryAction, setRetryAction] = useState<VoidFunction | null>(null);
  const [failureCount, setFailureCount] = useState<number>(0);
  const retryHandlerRef = useRef<VoidFunction | null>(null);

  const MAX_RETRIES = 3;

  const closeAlert = useCallback(() => {
    setIsOpen(false);
    setRetryAction(null);
  }, []);

  const resetFailureCount = useCallback(() => {
    setFailureCount(0);
  }, []);

  const showError = useCallback((message: string) => {
    setText(message);
    setRetryAction(null);
    setIsOpen(true);
  }, []);

  const checkMaxRetries = useCallback(
    (onMaxRetries?: VoidFunction) => {
      const nextCount = failureCount + 1;
      setFailureCount(nextCount);

      if (nextCount >= MAX_RETRIES) {
        setText(errorMessages.MAX_ATTEMPTS_OTP_VERIFY);
        if (onMaxRetries) {
          setRetryAction(() => () => {
            setIsOpen(false);
            onMaxRetries();
            setFailureCount(0);
          });
        } else {
          setRetryAction(null);
        }
        return true;
      }
      return false;
    },
    [failureCount]
  );

  const handleRetryableError = useCallback(
    (
      errorMessage: string,
      errorCode: number | string | undefined,
      onPreRetry?: VoidFunction,
      onMaxRetries?: VoidFunction
    ) => {
      const isError1801 =
        errorCode === ERROR_CODES.DOCUMENT_FETCH_FAILED ||
        (typeof errorMessage === 'string' && errorMessage.includes('1801'));

      if (isError1801) {
        const maxRetriesReached = checkMaxRetries(onMaxRetries);
        if (maxRetriesReached) {
          setIsOpen(true);
          return;
        }

        setText(errorMessage);
        const currentHandler = retryHandlerRef.current;
        if (currentHandler) {
          setRetryAction(() => () => {
            setIsOpen(false);
            if (onPreRetry) onPreRetry();
            currentHandler();
          });
        }
      } else {
        setText(errorMessage);
        setRetryAction(null);
      }

      setIsOpen(true);
    },
    [checkMaxRetries]
  );

  const handleTimeout = useCallback(
    (onPreRetry?: VoidFunction, onMaxRetries?: VoidFunction) => {
      const maxRetriesReached = checkMaxRetries(onMaxRetries);
      if (maxRetriesReached) {
        setIsOpen(true);
        return;
      }

      setText(errorMessages.QR_CODE_EXPIRE);

      const currentHandler = retryHandlerRef.current;
      if (currentHandler) {
        setRetryAction(() => () => {
          setIsOpen(false);
          if (onPreRetry) onPreRetry();
          currentHandler();
        });
      }

      setIsOpen(true);
    },
    [checkMaxRetries]
  );

  return {
    isOpen,
    text,
    retryAction,
    retryHandlerRef,
    closeAlert,
    showError,
    handleRetryableError,
    handleTimeout,
    setText,
    setIsOpen,
    resetFailureCount,
  };
};
